/**
 * Employer Alerts Center
 *
 * Proactive notifications and alerts:
 * - Risk spike alerts (employee moves to high-risk)
 * - Consent expiring notifications
 * - Program milestone achievements
 * - Engagement drop warnings
 * - System notifications
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Bell,
  AlertTriangle,
  CheckCircle2,
  Clock,
  TrendingDown,
  TrendingUp,
  Users,
  Gift,
  Shield,
  MessageSquare,
  X,
  Filter,
  RefreshCw,
  Loader2,
  ChevronRight,
  Eye,
  EyeOff,
  Trash2,
  Settings,
  Info,
  Star,
  Zap,
  Calendar,
  FileCheck,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface Alert {
  id: string;
  type: 'risk_spike' | 'consent_expiring' | 'milestone' | 'engagement_drop' | 'system' | 'success';
  severity: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
  dismissed: boolean;
  actionLabel?: string;
  actionTarget?: string;
  metadata?: {
    employeeCount?: number;
    employeeNames?: string[];
    programName?: string;
    daysRemaining?: number;
    percentChange?: number;
  };
}

interface AlertSummary {
  total: number;
  unread: number;
  critical: number;
  warning: number;
  info: number;
}

interface AlertPreferences {
  riskSpikes: boolean;
  consentExpiring: boolean;
  milestones: boolean;
  engagementDrops: boolean;
  systemNotifications: boolean;
  emailDigest: 'none' | 'daily' | 'weekly';
}

interface EmployerAlertsCenterProps {
  employerId: number;
  onNavigate?: (tab: string) => void;
}

export default function EmployerAlertsCenter({
  employerId,
  onNavigate,
}: EmployerAlertsCenterProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [showRead, setShowRead] = useState(true);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<AlertPreferences>({
    riskSpikes: true,
    consentExpiring: true,
    milestones: true,
    engagementDrops: true,
    systemNotifications: true,
    emailDigest: 'daily',
  });

  // Generate sample alerts (in production, these would come from an API)
  useEffect(() => {
    const generateAlerts = async () => {
      setLoading(true);
      try {
        // Fetch actual data if available
        const retentionUrl = getWeaveinApiUrl(`/api/employer/${employerId}/retention-risk`);
        const retentionResponse = await authFetch(retentionUrl);

        let highRiskCount = 0;
        if (retentionResponse.ok) {
          const data = await retentionResponse.json();
          highRiskCount = data.high_risk_count || 0;
        }

        const apiAlerts: Alert[] = [];

        // Only show real alerts from API data
        if (highRiskCount > 0) {
          apiAlerts.push({
            id: 'risk-1',
            type: 'risk_spike',
            severity: 'critical',
            title: 'Elevated Turnover Risk Detected',
            description: `${highRiskCount} employees have moved to high-risk category based on recent data analysis.`,
            timestamp: new Date().toISOString(),
            read: false,
            dismissed: false,
            actionLabel: 'View Details',
            actionTarget: 'employee-retention',
            metadata: { employeeCount: highRiskCount },
          });
        }

        setAlerts(apiAlerts);
      } catch (error) {
        console.error('Error loading alerts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (employerId) {
      generateAlerts();
    }
  }, [employerId, token]);

  // Calculate summary
  const summary = useMemo((): AlertSummary => {
    const activeAlerts = alerts.filter(a => !a.dismissed);
    return {
      total: activeAlerts.length,
      unread: activeAlerts.filter(a => !a.read).length,
      critical: activeAlerts.filter(a => a.severity === 'critical').length,
      warning: activeAlerts.filter(a => a.severity === 'warning').length,
      info: activeAlerts.filter(a => a.severity === 'info' || a.severity === 'success').length,
    };
  }, [alerts]);

  // Filtered alerts
  const filteredAlerts = useMemo(() => {
    return alerts
      .filter(a => !a.dismissed)
      .filter(a => showRead || !a.read)
      .filter(a => filterType === 'all' || a.type === filterType)
      .filter(a => filterSeverity === 'all' || a.severity === filterSeverity)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [alerts, showRead, filterType, filterSeverity]);

  const markAsRead = (alertId: string) => {
    setAlerts(prev =>
      prev.map(a => a.id === alertId ? { ...a, read: true } : a)
    );
  };

  const dismissAlert = (alertId: string) => {
    setAlerts(prev =>
      prev.map(a => a.id === alertId ? { ...a, dismissed: true } : a)
    );
  };

  const markAllAsRead = () => {
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'risk_spike':
        return <AlertTriangle className="w-5 h-5" />;
      case 'consent_expiring':
        return <Clock className="w-5 h-5" />;
      case 'milestone':
        return <Star className="w-5 h-5" />;
      case 'engagement_drop':
        return <TrendingDown className="w-5 h-5" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          icon: 'text-red-500',
          badge: 'bg-red-100 text-red-700',
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          border: 'border-amber-200 dark:border-amber-800',
          icon: 'text-amber-500',
          badge: 'bg-amber-100 text-amber-700',
        };
      case 'success':
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          icon: 'text-green-500',
          badge: 'bg-green-100 text-green-700',
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          icon: 'text-blue-500',
          badge: 'bg-blue-100 text-blue-700',
        };
    }
  };

  const formatTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return fmt.short(timestamp);
  };

  const handleNavigate = (tab: string) => {
    if (onNavigate) {
      onNavigate(tab);
    } else {
      const event = new CustomEvent('changeTab', { detail: tab });
      window.dispatchEvent(event);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('employer.alerts.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl relative">
            <Bell className="h-6 w-6 text-white" />
            {summary.unread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {summary.unread}
              </span>
            )}
          </div>
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('employer.alerts.title')}
            </h1>
            )}
            <p className="text-gray-600 dark:text-gray-400">
              {t('employer.alerts.unreadAlertsCount', { count: summary.unread })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            disabled={summary.unread === 0}
          >
            <Eye className="w-4 h-4 mr-2" />
            {t('employer.alerts.markAllRead')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreferences(!showPreferences)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={summary.critical > 0 ? 'border-red-200 bg-red-50/50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-sm text-muted-foreground">{t('employer.alerts.critical')}</span>
            </div>
            <div className={`text-2xl font-bold ${summary.critical > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {summary.critical}
            </div>
          </CardContent>
        </Card>

        <Card className={summary.warning > 0 ? 'border-amber-200 bg-amber-50/50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-muted-foreground">{t('employer.alerts.warnings')}</span>
            </div>
            <div className={`text-2xl font-bold ${summary.warning > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {summary.warning}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">{t('employer.alerts.info')}</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {summary.info}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Bell className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-muted-foreground">{t('employer.alerts.totalActive')}</span>
            </div>
            <div className="text-2xl font-bold text-gray-600">
              {summary.total}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preferences Panel */}
      {showPreferences && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="w-5 h-5 text-purple-600" />
              {t('employer.alerts.preferencesTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{t('employer.alerts.alertTypes')}</h4>
                {[
                  { key: 'riskSpikes', label: t('employer.alerts.riskSpikeAlerts'), icon: AlertTriangle },
                  { key: 'consentExpiring', label: t('employer.alerts.consentExpiringLabel'), icon: Clock },
                  { key: 'milestones', label: t('employer.alerts.milestoneAchievements'), icon: Star },
                  { key: 'engagementDrops', label: t('employer.alerts.engagementDrops'), icon: TrendingDown },
                  { key: 'systemNotifications', label: t('employer.alerts.systemNotifications'), icon: Info },
                ].map(({ key, label, icon: Icon }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences[key as keyof AlertPreferences] as boolean}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        [key]: e.target.checked,
                      }))}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('employer.alerts.emailDigest')}</h4>
                <select
                  value={preferences.emailDigest}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    emailDigest: e.target.value as 'none' | 'daily' | 'weekly',
                  }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="none">{t('employer.alerts.noEmailNotifications')}</option>
                  <option value="daily">{t('employer.alerts.dailyDigest')}</option>
                  <option value="weekly">{t('employer.alerts.weeklyDigest')}</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-sm"
        >
          <option value="all">{t('employer.alerts.allTypes')}</option>
          <option value="risk_spike">{t('employer.alerts.riskSpikes')}</option>
          <option value="consent_expiring">{t('employer.alerts.consentExpiring')}</option>
          <option value="milestone">{t('employer.alerts.milestones')}</option>
          <option value="engagement_drop">{t('employer.alerts.engagement')}</option>
          <option value="system">{t('employer.alerts.system')}</option>
        </select>

        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-sm"
        >
          <option value="all">{t('employer.alerts.allSeverities')}</option>
          <option value="critical">{t('employer.alerts.critical')}</option>
          <option value="warning">{t('employer.alerts.warning')}</option>
          <option value="info">{t('employer.alerts.info')}</option>
          <option value="success">{t('employer.alerts.success')}</option>
        </select>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowRead(!showRead)}
          className={showRead ? '' : 'text-purple-600'}
        >
          {showRead ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
          {showRead ? t('employer.alerts.hideRead') : t('employer.alerts.showRead')}
        </Button>
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {filteredAlerts.length > 0 ? (
          filteredAlerts.map((alert) => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <div
                key={alert.id}
                className={`
                  p-4 rounded-lg border transition-all
                  ${styles.bg} ${styles.border}
                  ${!alert.read ? 'ring-2 ring-offset-2 ring-purple-200' : ''}
                `}
                onClick={() => markAsRead(alert.id)}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg bg-white dark:bg-gray-800 ${styles.icon}`}>
                    {getTypeIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {alert.title}
                      </h4>
                      {!alert.read && (
                        <span className="w-2 h-2 rounded-full bg-purple-600" />
                      )}
                      <Badge className={styles.badge}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {alert.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{formatTimeAgo(alert.timestamp)}</span>
                      {alert.metadata?.employeeCount && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {t('employer.alerts.employeesCount', { count: alert.metadata.employeeCount })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {alert.actionLabel && alert.actionTarget && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate(alert.actionTarget!);
                        }}
                      >
                        {alert.actionLabel}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissAlert(alert.id);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('employer.alerts.noAlertsToShow')}
            </h4>
            <p className="text-gray-600 dark:text-gray-400">
              {filterType !== 'all' || filterSeverity !== 'all'
                ? t('employer.alerts.tryAdjustingFilters')
                : t('employer.alerts.allCaughtUp')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
